import type { ButtonHTMLAttributes, FC, ReactNode } from 'react'
import { isValidElement } from 'react'
import { cn } from '@/lib/utils'
import { isReactComponent } from '@/lib/utils/is-react-component'

interface OnboardingButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Shows a loading spinner and disables the button */
  isLoading?: boolean
  /** Disables the button */
  isDisabled?: boolean
  /** Icon component or element to show after the text */
  iconTrailing?: FC<{ className?: string }> | ReactNode
  /** Full width button (default: true) */
  fullWidth?: boolean
  children: ReactNode
}

export function OnboardingButton({
  children,
  className,
  isLoading,
  isDisabled,
  iconTrailing: IconTrailing,
  fullWidth = true,
  type = 'button',
  ...props
}: OnboardingButtonProps) {
  const disabled = isDisabled || isLoading

  return (
    <button
      type={type}
      disabled={disabled}
      className={cn(
        'relative inline-flex cursor-pointer items-center justify-center gap-1.5 rounded-lg px-4 py-2.5 text-md font-semibold transition-colors',
        'bg-[#0a0d12] border-2 border-[rgba(255,255,255,0.12)] text-white',
        'hover:bg-[#1a1d22]',
        'disabled:cursor-not-allowed disabled:opacity-50',
        fullWidth && 'w-full',
        className
      )}
      {...props}
    >
      {isLoading && (
        <svg
          fill="none"
          viewBox="0 0 20 20"
          className={cn(
            'pointer-events-none size-5 shrink-0',
            'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'
          )}
        >
          <circle
            className="stroke-current opacity-30"
            cx="10"
            cy="10"
            r="8"
            fill="none"
            strokeWidth="2"
          />
          <circle
            className="origin-center animate-spin stroke-current"
            cx="10"
            cy="10"
            r="8"
            fill="none"
            strokeWidth="2"
            strokeDasharray="12.5 50"
            strokeLinecap="round"
          />
        </svg>
      )}

      <span className={cn('px-0.5', isLoading && 'invisible')}>{children}</span>

      {IconTrailing && !isLoading && (
        <>
          {isValidElement(IconTrailing) && IconTrailing}
          {isReactComponent(IconTrailing) && (
            <IconTrailing className="pointer-events-none size-5 shrink-0" />
          )}
        </>
      )}
    </button>
  )
}
